/** Whiteboard model: strokes serialized over the `whiteboard` data topic. Pure + testable. */

export interface BoardPoint {
  x: number;
  y: number;
}

export interface BoardStroke {
  id: string;
  color: string;
  width: number;
  eraser: boolean;
  author: number;
  points: BoardPoint[];
}

export type Board = BoardStroke[];

export const BOARD_PALETTE = [
  '#1f6feb',
  '#e5534b',
  '#2da44e',
  '#bf8700',
  '#8250df',
  '#0d1117',
] as const;
export const MIN_WIDTH = 1;
export const MAX_WIDTH = 24;

export function createStroke(input: {
  author: number;
  color?: string;
  width?: number;
  eraser?: boolean;
  point: BoardPoint;
}): BoardStroke {
  return {
    id: `st_${Math.random().toString(36).slice(2, 10)}`,
    color: input.color ?? BOARD_PALETTE[0],
    width: clampWidth(input.width ?? 3),
    eraser: Boolean(input.eraser),
    author: input.author,
    points: [{ x: clamp(input.point.x), y: clamp(input.point.y) }],
  };
}

export function addPoint(stroke: BoardStroke, point: BoardPoint): BoardStroke {
  const last = stroke.points[stroke.points.length - 1];
  const normalized = { x: clamp(point.x), y: clamp(point.y) };
  if (last && last.x === normalized.x && last.y === normalized.y) return stroke;
  return { ...stroke, points: [...stroke.points, normalized] };
}

export function appendStroke(board: Board, stroke: BoardStroke, limit = 500): Board {
  if (stroke.points.length === 0) return board;
  const next = [...board, stroke];
  return next.length > limit ? next.slice(next.length - limit) : next;
}

export function undoLastStroke(board: Board, author?: number): Board {
  if (board.length === 0) return board;
  if (author === undefined) return board.slice(0, -1);
  for (let index = board.length - 1; index >= 0; index -= 1) {
    if (board[index]!.author === author) return board.filter((_, position) => position !== index);
  }
  return board;
}

export function clearBoard(): Board {
  return [];
}

/** Serialize one stroke for the data channel; used both for live strokes and late-join snapshots. */
export function encodeStroke(stroke: BoardStroke): Uint8Array {
  return new TextEncoder().encode(JSON.stringify({ type: 'board:stroke', stroke }));
}

export function decodeBoardMessage(
  payload: Uint8Array,
  from: number,
): { stroke: BoardStroke } | { snapshot: Board } | { clear: true } | null {
  let parsed: { type?: string; stroke?: BoardStroke; board?: Board };
  try {
    parsed = JSON.parse(new TextDecoder().decode(payload)) as typeof parsed;
  } catch {
    return null;
  }
  if (parsed.type === 'board:stroke' && isStroke(parsed.stroke)) {
    return { stroke: { ...parsed.stroke, author: parsed.stroke.author || from } };
  }
  if (parsed.type === 'board:snapshot' && Array.isArray(parsed.board)) {
    return { snapshot: parsed.board.filter(isStroke) };
  }
  if (parsed.type === 'board:clear') return { clear: true };
  return null;
}

export function isStroke(value: unknown): value is BoardStroke {
  if (typeof value !== 'object' || value === null) return false;
  const stroke = value as Partial<BoardStroke>;
  return (
    typeof stroke.id === 'string' &&
    typeof stroke.color === 'string' &&
    typeof stroke.width === 'number' &&
    typeof stroke.eraser === 'boolean' &&
    Array.isArray(stroke.points) &&
    stroke.points.every((point) => typeof point?.x === 'number' && typeof point?.y === 'number')
  );
}

function clamp(value: number): number {
  return Math.min(1, Math.max(0, Number.isFinite(value) ? value : 0));
}

function clampWidth(width: number): number {
  return Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, width));
}
